import { useCallback, useEffect, useState } from 'react';
import { ApiError, NotSignedInError } from './api';
import { ConfirmDialog } from './kit/dialogs';

/** Small pieces shared by every page. */

export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  replace: (next: T) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        // A dead session is not this page's problem — the shell watches for it and shows
        // sign-in, so surfacing it here would be a second, redundant error.
        if (failure instanceof NotSignedInError) return;
        setError(describe(failure));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  /*
   * Stable, and that is part of the contract rather than an optimisation.
   *
   * `reload` gets passed down as a prop and named in dependency lists. A fresh arrow on every
   * render makes every one of those lists change on every render — so an effect that lists it
   * re-runs continuously, and if that effect *also* calls it, the two feed each other. The
   * Playground hit exactly this and looped through `POST /playground/session`, the one endpoint
   * in this product that spends money, several times a second.
   *
   * `setNonce` is stable, so an empty dependency list here is correct and permanent.
   */
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  /**
   * For a write that already answered with the new state. Showing that answer is one round trip
   * sooner than `reload`, and nothing in between shows the state from before the write — which a
   * button reads as "you can press me again". Stable, for the same reason `reload` is.
   */
  const replace = useCallback((next: T) => {
    setData(next);
    setError(null);
  }, []);

  return { data, error, loading, reload, replace };
}

/**
 * Whether the window matches a media query, kept current as it is resized or turned.
 *
 * For a decision the page makes in code rather than in `kit.css`: what is *mounted* rather than
 * how it is drawn — the creator onboarding, which a phone never asks for, and the playground,
 * which a phone never starts.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/**
 * A window large enough for a workspace: the dashboard's own breakpoint (`max-width: 900px`
 * throughout `kit.css` is where its navigation column gives way), and tall enough that a phone on
 * its side, which can be wider than that, does not count.
 */
export const LARGE_SCREEN_QUERY = '(min-width: 901px) and (min-height: 501px)';

/** The server writes its messages to be read by a person; show them as they are. */
export function describe(failure: unknown): string {
  if (failure instanceof ApiError) {
    const fields = fieldIssues(failure.details);
    return fields ? `${failure.message} ${fields}` : failure.message;
  }
  if (failure instanceof Error) return failure.message;
  return String(failure);
}

/**
 * The per-field half of a validation failure, as `lib/validate.ts` flattens it.
 *
 * On its own the summary reads "Request body failed validation.", which is true and no use at
 * all to someone who typed a capital letter into a package prefix. The detail naming the
 * field is the only part of that response worth reading.
 */
function fieldIssues(details: unknown): string {
  if (!Array.isArray(details)) return '';
  return details
    .filter(
      (issue): issue is { path?: string; message: string } =>
        typeof issue === 'object' &&
        issue !== null &&
        typeof (issue as { message?: unknown }).message === 'string',
    )
    .map((issue) => (issue.path ? `${issue.path}: ${issue.message}` : issue.message))
    .join(' ');
}

export function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return <div className="error">{error}</div>;
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="muted small">{children}</p>;
}

/** `1.4.2 (142)` — always both, because two builds named "0.1.0" are otherwise the same build. */
export function version(versionName: string, versionCode: number) {
  return (
    <span className="bf-inline">
      <span className="bf-strong">{versionName}</span>
      <span className="bf-mono">({versionCode})</span>
    </span>
  );
}

/**
 * A date and a time: "14 Aug 2026, 09:12".
 *
 * Same fixed format as `day`, with a 24-hour clock. The browser's locale would render this
 * as "8/14/2026, 2:12 AM" for some readers and "14/08/2026, 02:12" for others, and a build
 * timeline is read by people comparing notes across a team.
 */
export function when(epochMillis: number | null | undefined): string {
  if (!epochMillis) return '—';
  return new Date(epochMillis).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * A date with no time: "14 Aug 2026".
 *
 * Fixed to en-GB rather than the browser's locale, so every reader of the same build sees the
 * same date. A developer comparing what the dashboard says against what a tester is looking at
 * should not have to work out whether 08/09 is a September build or an August one.
 */
export function day(epochMillis: number | null | undefined): string {
  if (!epochMillis) return '—';
  return new Date(epochMillis).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * "just now", "3h ago", "12 Aug" — how long ago, for a list read newest-first.
 *
 * The feedback tab is the one list where the useful question is "is this still happening?"
 * rather than "which build was this?", and an exact timestamp answers that more slowly than a
 * glance should take. Every caller pairs it with `when()` in a `title`, so the precise time is
 * one hover away and nothing is actually lost.
 */
export function since(epochMillis: number | null | undefined, now: number = Date.now()): string {
  if (!epochMillis) return '—';
  const seconds = Math.max(0, Math.round((now - epochMillis) / 1000));
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  // Past a week the count stops meaning anything to anyone — "23d ago" is a date said badly.
  return days <= 7 ? `${days}d ago` : day(epochMillis);
}

export function bytes(size: number): string {
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = size / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export interface ConfirmRequest {
  title: string;
  /** Prose, not a label: what is taken away, and from whom. */
  body: React.ReactNode;
  /** The button that goes through with it — a verb, never "OK". */
  confirmLabel: string;
  danger?: boolean;
  /**
   * Typed back exactly before the button enables, for the handful of actions with nothing
   * behind them to undo the damage. Use the name of the thing being destroyed, so the typing
   * is a second reading of *what* rather than a captcha.
   */
  typeToConfirm?: string;
}

/**
 * A destructive action that states what it destroys, as a promise.
 *
 * Everything irreversible in this product — deleting a build, revoking a code, removing a
 * member — takes away something someone else depends on, so the confirmation names it rather
 * than asking "are you sure?". This is the only confirmation pattern in the dashboard, and it
 * replaced `window.confirm`, which gets one line of unstyled text in browser chrome: the wrong
 * shape for a decision whose whole content is the list of what goes with it, and unable to ask
 * for anything, so the irreversible actions and the recoverable ones looked identical.
 *
 * Returns the asker and the dialog to render — the caller places it, because a dialog that
 * mounted itself somewhere global would need a provider this app does not otherwise have.
 */
export function useConfirmModal(): {
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  dialog: React.ReactNode;
} {
  const [pending, setPending] = useState<{
    request: ConfirmRequest;
    settle: (confirmed: boolean) => void;
  } | null>(null);

  const settle = (confirmed: boolean) => {
    pending?.settle(confirmed);
    setPending(null);
  };

  return {
    confirm: (request) => new Promise<boolean>((resolve) => setPending({ request, settle: resolve })),
    dialog: (
      <ConfirmDialog
        open={pending !== null}
        title={pending?.request.title ?? ''}
        confirmLabel={pending?.request.confirmLabel}
        destructive={pending?.request.danger ?? true}
        typeToConfirm={pending?.request.typeToConfirm}
        onCancel={() => settle(false)}
        onConfirm={() => settle(true)}
      >
        {pending?.request.body}
      </ConfirmDialog>
    ),
  };
}

/**
 * A cursor-paged list.
 *
 * The API returns `nextCursor` on builds, history and activity; ignoring it made every list
 * stop at the page size and look complete, which is the kind of wrong that only shows up
 * once there is enough data to matter.
 */
export function usePaged<T>(
  load: (cursor: string | null) => Promise<{ items: T[]; nextCursor: string | null }>,
  deps: unknown[],
): {
  items: T[];
  error: string | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
  /**
   * Change what is loaded where it stands, without reading the list again — a row edited in place.
   * A reload shows "Loading…" over the list, which is right for a new filter and wrong for one
   * row's status.
   */
  update: (change: (items: T[]) => T[]) => void;
} {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load(null)
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (!cancelled && !(failure instanceof NotSignedInError)) setError(describe(failure));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return {
    items,
    error,
    loading,
    loadingMore,
    hasMore,
    reload: () => setNonce((n) => n + 1),
    update: setItems,
    loadMore: () => {
      if (!cursor || loadingMore) return;
      setLoadingMore(true);
      load(cursor)
        .then((page) => {
          setItems((current) => [...current, ...page.items]);
          setCursor(page.nextCursor);
          setHasMore(page.nextCursor !== null);
        })
        .catch((failure: unknown) => setError(describe(failure)))
        .finally(() => setLoadingMore(false));
    },
  };
}

export function LoadMore({
  hasMore,
  loading,
  onClick,
}: {
  hasMore: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className="row" style={{ marginTop: 12 }}>
      <button onClick={onClick} disabled={loading}>
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div>
      <label>{label}</label>
      {children}
      {hint ? <div className="muted small" style={{ marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}
